import type { GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import React from 'react';
import styled from 'styled-components';

import { EntryContentTypeFieldsEnum, REVALIDATE_INTERVAL } from '@seuk/cms-api-client';
import { Container, Main } from '@seuk/design-system/atoms';
import { Breadcrumbs } from '@seuk/design-system/molecules';
import { DEFAULT_LOCALE } from '@seuk/shared-data';

import { PageHeader } from '../../../components/page-header';
import PageItem from '../../../components/text-page-item';
import TextPageSectionItem from '../../../components/text-page-item-section';
import { getCMSClient } from '../../../services/cms';
import type { TextItem } from '../../../util/section';
import { richTextSectionItemMapper } from '../../../util/section';
import type { SEO } from '../../../util/seo';
import { extractCMSSEO } from '../../../util/seo';

type UrlParams = {
  articleUrlName: string;
};

interface PressArticlePageProps {
  title: string;
  sections?: readonly TextItem[];
  seo: SEO;
}

const StyledMain = styled(Main)`
  img {
    // limit embedded image sizes
    max-width: ${({ theme }) => theme.spacing(120)};
    max-height: ${({ theme }) => theme.spacing(80)};
    height: auto;
    width: auto;
  }
`;

export default function PressPage({ sections, seo, title }: PressArticlePageProps) {
  const breadcrumbItems = React.useMemo(() => {
    return [{ label: 'Home', href: '/' }, { label: 'Press', href: '/press' }, { label: title }];
  }, [title]);

  return (
    <>
      <NextSeo title={seo?.title} description={seo?.description} nofollow={seo?.nofollow} noindex={seo?.noindex} />

      <StyledMain>
        <Container>
          <Breadcrumbs items={breadcrumbItems} backHref="/press" />
        </Container>
        <PageHeader title={title} />
        <Container>
          {sections.map((section) => (
            <PageItem title={section.title} key={section.id}>
              <TextPageSectionItem item={section} trackInfo={{ pageTitle: title }} />
            </PageItem>
          ))}
        </Container>
      </StyledMain>
    </>
  );
}

export const getStaticProps: GetStaticProps<PressArticlePageProps, UrlParams> = async ({ preview, params }) => {
  const entry = await getCMSClient(preview).getEntryByFieldValue(
    EntryContentTypeFieldsEnum.PagePressArticle,
    { slug: params.articleUrlName },
    { locale: DEFAULT_LOCALE }
  );

  if (!entry) {
    throw new Error(`No press article defined ${params.articleUrlName}`);
  }

  const props: PressArticlePageProps = {
    title: entry.fields.name,
    seo: entry.fields.seo ? extractCMSSEO(entry.fields.seo.fields) : null,
    sections: await Promise.all(entry.fields.sections?.map(richTextSectionItemMapper) ?? []),
  };

  return {
    props,
    revalidate: REVALIDATE_INTERVAL,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const entries = await getCMSClient().getAllEntries({ contentType: EntryContentTypeFieldsEnum.PagePressArticle });
  return {
    paths: entries.map(({ fields }) => ({
      params: { articleUrlName: fields.slug },
    })),
    fallback: 'blocking',
  };
};
